import { Calendar3Event, Calendar3Week } from '@styled-icons/bootstrap';
import OriginBatchTracingContainer from 'components/Shared/BatchTracingContainer/OriginBatchTracingContainer';
import { ButtonOutlinedPrimary } from 'components/Shared/Button';
import Icon from 'components/Shared/Icon/Icon';
import { MAP_TRANSPORT_ICONS, MAP_TRANSPORT_TRANSLATION_KEYS, } from 'constants/map';
import { useBrowser } from 'hooks';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SiteType } from 'types/site.types';
import { getPrimaryImage } from 'utils/supplyChain';
import BatchTracingContainer from './BatchTracingContainer';
import BatchHeader from './BatchHeader';
import { Container } from './styles';
import { transformNumberToLocaleString } from 'utils/numbers';
import NoDataContainer from './NoDataContainer';
const DATE_FORMAT = 'YYYY-MM-DD';
const BatchTracing = ({ selectedSiteId, batchTracingActive, rawMaterial, chainStepActivity, hideLocationPin, hasOtherIngredients, isOverview, notTraceableStep, notTraceableActivity, onBatchTracingStart, onBatchTracingEnd, onClick, onGoToFirstBatchDataStep, }) => {
    var _a, _b, _c, _d;
    const { t } = useTranslation();
    const { isMobile } = useBrowser();
    if (batchTracingActive && !rawMaterial) {
        return (React.createElement(Container, { isMobile: isMobile },
            React.createElement(BatchHeader, { noRawMaterialSelected: true, hasOtherIngredients: hasOtherIngredients })));
    }
    if (batchTracingActive && notTraceableStep) {
        return (React.createElement(NoDataContainer, { isMobile: isMobile, infoMessage: t('no-batch-data-for-step'), rawMaterialTitle: rawMaterial.title, onGoToFirstBatchDataStep: onGoToFirstBatchDataStep, onClose: onBatchTracingEnd }));
    }
    if (batchTracingActive && notTraceableActivity) {
        return (React.createElement(NoDataContainer, { isMobile: isMobile, infoMessage: t('activity-not-in-batch'), rawMaterialTitle: rawMaterial.title, onClose: onBatchTracingEnd }));
    }
    if (!batchTracingActive && !isMobile) {
        return (React.createElement(ButtonOutlinedPrimary, { onClick: onBatchTracingStart },
            React.createElement(Icon, { name: "tracing" }),
            t('trace-my-batch')));
    }
    const activityBatchDetails = chainStepActivity.batchDetails;
    const site = chainStepActivity.activity.site;
    const isOriginStep = (site === null || site === void 0 ? void 0 : site.siteType) === SiteType.ORIGIN_SITE ||
        (site === null || site === void 0 ? void 0 : site.siteType) === SiteType.FARM ||
        (site === null || site === void 0 ? void 0 : site.siteType) === SiteType.CLUSTER ||
        (site === null || site === void 0 ? void 0 : site.siteType) === SiteType.MINE;
    if (isOriginStep && activityBatchDetails) {
        if (activityBatchDetails.amountOfFarms === undefined &&
            activityBatchDetails.totalHarvestAmount === undefined) {
            return React.createElement(React.Fragment, null);
        }
        if ((site.siteType === SiteType.CLUSTER || site.siteType === SiteType.FARM) &&
            selectedSiteId) {
            const selectedSite = site.siteType === SiteType.CLUSTER
                ? (_a = site.sites.edges.find(({ node: { id } }) => id === selectedSiteId)) === null || _a === void 0 ? void 0 : _a.node
                : site;
            const selectedSiteBatchData = selectedSite.batchDetails;
            // Empty farm batch data
            if (!selectedSiteBatchData) {
                return (React.createElement(OriginBatchTracingContainer, { selectedRawMaterial: rawMaterial, activityImage: getPrimaryImage(chainStepActivity.activity.mediaList), hideLocationPin: hideLocationPin, hasOtherIngredients: hasOtherIngredients, items: [
                        {
                            label: t('from-this-farm'),
                            value: '0 kg',
                        },
                        {
                            label: t('farm-yield'),
                            value: '0 kg/ha',
                        },
                    ], subItems: isOverview
                        ? undefined
                        : [
                            {
                                label: t('harvest-period'),
                                labelIcon: React.createElement(Calendar3Week, { size: 13 }),
                            },
                            {
                                label: t('date-pickup'),
                                labelIcon: React.createElement(Calendar3Event, { size: 13 }),
                            },
                            {
                                label: t('harvest-pickup'),
                            },
                        ], onClose: onBatchTracingEnd, onClick: onClick }));
            }
            return (React.createElement(OriginBatchTracingContainer, { selectedRawMaterial: rawMaterial, activityImage: getPrimaryImage(chainStepActivity.activity.mediaList), hideLocationPin: hideLocationPin, hasOtherIngredients: hasOtherIngredients, items: [
                    {
                        label: t('from-this-farm'),
                        value: `${transformNumberToLocaleString(selectedSiteBatchData === null || selectedSiteBatchData === void 0 ? void 0 : selectedSiteBatchData.totalHarvestAmount)} kg`,
                    },
                    {
                        label: t('farm-yield'),
                        value: (selectedSiteBatchData === null || selectedSiteBatchData === void 0 ? void 0 : selectedSiteBatchData.harvestYield)
                            ? `${transformNumberToLocaleString(selectedSiteBatchData === null || selectedSiteBatchData === void 0 ? void 0 : selectedSiteBatchData.harvestYield)} kg/ha`
                            : t('unknown'),
                    },
                ], subItems: isOverview
                    ? undefined
                    : [
                        ...((selectedSiteBatchData === null || selectedSiteBatchData === void 0 ? void 0 : selectedSiteBatchData.harvestPeriod)
                            ? [
                                {
                                    label: t('harvest-period'),
                                    value: `${moment((_b = selectedSiteBatchData === null || selectedSiteBatchData === void 0 ? void 0 : selectedSiteBatchData.harvestPeriod) === null || _b === void 0 ? void 0 : _b.start).format(DATE_FORMAT)} - ${moment((_c = selectedSiteBatchData === null || selectedSiteBatchData === void 0 ? void 0 : selectedSiteBatchData.harvestPeriod) === null || _c === void 0 ? void 0 : _c.end).format(DATE_FORMAT)}`,
                                    labelIcon: React.createElement(Calendar3Week, { size: 13 }),
                                },
                            ]
                            : []),
                        {
                            label: t('date-pickup'),
                            value: `${moment(selectedSiteBatchData === null || selectedSiteBatchData === void 0 ? void 0 : selectedSiteBatchData.dateOfPickup).format(DATE_FORMAT)}`,
                            labelIcon: React.createElement(Calendar3Event, { size: 13 }),
                        },
                        ...((selectedSiteBatchData === null || selectedSiteBatchData === void 0 ? void 0 : selectedSiteBatchData.harvestPickupMode)
                            ? [
                                {
                                    label: t('harvest-pickup'),
                                    value: t(MAP_TRANSPORT_TRANSLATION_KEYS[selectedSiteBatchData === null || selectedSiteBatchData === void 0 ? void 0 : selectedSiteBatchData.harvestPickupMode]),
                                    valueIcon: (React.createElement("img", { width: 17, src: (_d = MAP_TRANSPORT_ICONS[selectedSiteBatchData === null || selectedSiteBatchData === void 0 ? void 0 : selectedSiteBatchData.harvestPickupMode]) === null || _d === void 0 ? void 0 : _d.url })),
                                },
                            ]
                            : []),
                    ], onClose: onBatchTracingEnd, onClick: onClick }));
        }
        return (React.createElement(OriginBatchTracingContainer, { selectedRawMaterial: rawMaterial, hideLocationPin: hideLocationPin, hasOtherIngredients: hasOtherIngredients, activityImage: getPrimaryImage(chainStepActivity.activity.mediaList), items: [
                {
                    label: t('from-this-origin'),
                    value: `${transformNumberToLocaleString(activityBatchDetails.totalHarvestAmount)} kg`,
                },
                {
                    label: t('farms-produced'),
                    value: `${activityBatchDetails.amountOfFarms || 0} ${activityBatchDetails.amountOfFarms === 1 ? t('farm').toLocaleLowerCase() : t('farms')} `,
                },
            ], onClose: onBatchTracingEnd, onClick: onClick }));
    }
    if (chainStepActivity.siteBatchDetails) {
        const siteBatchDetails = chainStepActivity.siteBatchDetails;
        // filter out all siteBatchDetails that don't have the rawMaterialId equal to the selected rawMaterial
        const siteBatchDetailsFiltered = siteBatchDetails.filter((siteBatchDetail) => siteBatchDetail.rawMaterialId === (rawMaterial === null || rawMaterial === void 0 ? void 0 : rawMaterial.id));
        if (siteBatchDetailsFiltered.length === 0)
            return React.createElement(React.Fragment, null);
        const hasInboundDate = !!siteBatchDetailsFiltered[0].dateReceived;
        const hasOutboundDate = !!siteBatchDetailsFiltered[siteBatchDetailsFiltered.length - 1]
            .dateDispatched;
        const inbound = siteBatchDetailsFiltered[0];
        const outbound = siteBatchDetailsFiltered[siteBatchDetailsFiltered.length - 1];
        // slice array based on hasInboundDate and hasOutboundDate
        const subActivities = siteBatchDetailsFiltered.slice(hasInboundDate ? 1 : 0, hasOutboundDate
            ? siteBatchDetailsFiltered.length - 1
            : siteBatchDetailsFiltered.length);
        return (React.createElement(BatchTracingContainer, { selectedRawMaterial: rawMaterial, hasOtherIngredients: hasOtherIngredients, activityImage: getPrimaryImage(chainStepActivity.activity.mediaList), inboundWeight: inbound.totalInboundWeight, outboundWeight: outbound.totalOutboundWeight, dateReceived: hasInboundDate
                ? moment(inbound.dateReceived).format(DATE_FORMAT)
                : t('no-transport'), dateDispatched: hasOutboundDate
                ? moment(outbound.dateDispatched).format(DATE_FORMAT)
                : t('no-transport'), batchActivities: subActivities.map(({ siteActivityTitle, dateOfActivity }) => ({
                title: siteActivityTitle,
                date: moment(dateOfActivity).format(DATE_FORMAT),
            })), isOverview: isOverview, onClose: onBatchTracingEnd, onClick: onClick }));
    }
    const rawMaterialBatchDetails = rawMaterial === null || rawMaterial === void 0 ? void 0 : rawMaterial.batchDetails;
    if (rawMaterialBatchDetails) {
        if (!rawMaterialBatchDetails.amountOfFarms &&
            !rawMaterialBatchDetails.totalHarvestAmount) {
            return (React.createElement(NoDataContainer, { isMobile: isMobile, infoMessage: t('no-batch-data-for-activity'), rawMaterialTitle: rawMaterial.title, onClose: onBatchTracingEnd }));
        }
        if (!rawMaterialBatchDetails.amountOfFarms ||
            !rawMaterialBatchDetails.totalHarvestAmount)
            return React.createElement(React.Fragment, null);
        return (React.createElement(OriginBatchTracingContainer, { selectedRawMaterial: rawMaterial, activityImage: getPrimaryImage(chainStepActivity.activity.mediaList), items: [
                {
                    label: t('from-this-origin'),
                    value: `${rawMaterialBatchDetails.amountOfFarms} ${t('farms')}`,
                },
                {
                    label: t('farms-produced'),
                    value: `${transformNumberToLocaleString(rawMaterialBatchDetails.totalHarvestAmount)} kg`,
                },
            ], onClose: onBatchTracingEnd, onClick: onClick }));
    }
    return React.createElement(React.Fragment, null);
};
export default BatchTracing;
